import React, { useMemo, useEffect } from 'react';
import { Container, Box, Typography, Divider, Link, List, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();

  const lastUpdated = useMemo(() => {
    try {
      return new Date().toLocaleDateString(i18n.language?.startsWith('uk') ? 'uk-UA' : 'en-CA', {
        year: 'numeric', month: 'long', day: '2-digit'
      });
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  }, [i18n.language]);

  useEffect(() => {
    document.title = `${t('privacy.title')} — ${t('library')}`;
  }, [t]);

  const sections = [
    { id: 'scope', key: 'scope' },
    { id: 'data', key: 'data' },
    { id: 'sources', key: 'sources' },
    { id: 'purposes', key: 'purposes' },
    { id: 'legal', key: 'legal' },
    { id: 'sharing', key: 'sharing' },
    { id: 'retention', key: 'retention' },
    { id: 'security', key: 'security' },
    { id: 'rights', key: 'rights' },
    { id: 'children', key: 'children' },
    { id: 'cookies', key: 'cookies' },
    { id: 'thirdparties', key: 'thirdparties' },
    { id: 'liability', key: 'liability' },
    { id: 'changes', key: 'changes' },
    { id: 'contact', key: 'contact' }
  ];

  const list = (path) => {
    const val = t(path, { returnObjects: true, defaultValue: [] });
    if (Array.isArray(val)) return val;
    if (val == null) return [];
    if (typeof val === 'string') {
      const s = val.trim();
      return s && s !== path ? [s] : [];
    }
    return [];
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Box textAlign="center" mb={2}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
          {t('privacy.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('privacy.lastUpdated', { date: lastUpdated })}
        </Typography>
      </Box>

      <Box sx={{ p: 2, bgColor: 'background.paper', borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 1 }}>
          {t('privacy.toc')}
        </Typography>
        <Box component="nav" aria-label={t('privacy.toc')}>
          <List dense sx={{ columns: { xs: 1, sm: 2 }, columnGap: 4, listStyle: 'none', p: 0, m: 0 }}>
            {sections.map(({ id, key }) => (
              <ListItem key={id} sx={{ breakInside: 'avoid', display: 'list-item', pl: 0 }}>
                <Link href={`#${id}`} underline="hover">{t(`privacy.sections.${key}.title`)}</Link>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>{t('privacy.intro')}</Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>{t('privacy.notTerms')}</Typography>

      <Divider sx={{ my: 2 }} />

      {/* Scope */}
      <Box id="scope" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.scope.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.scope.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Data we collect */}
      <Box id="data" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.data.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.data.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Sources */}
      <Box id="sources" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.sources.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.sources.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Purposes */}
      <Box id="purposes" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.purposes.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.purposes.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Legal basis */}
      <Box id="legal" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.legal.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.legal.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Sharing */}
      <Box id="sharing" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.sharing.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.sharing.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Retention & Location */}
      <Box id="retention" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.retention.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.retention.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Security */}
      <Box id="security" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.security.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>{t('privacy.sections.security.body')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.security.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Rights */}
      <Box id="rights" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.rights.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.rights.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Children */}
      <Box id="children" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.children.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.children.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Cookies */}
      <Box id="cookies" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.cookies.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('privacy.sections.cookies.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Third-party links */}
      <Box id="thirdparties" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.thirdparties.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.thirdparties.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Liability / Disclaimers */}
      <Box id="liability" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.liability.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.liability.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Changes */}
      <Box id="changes" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.changes.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.changes.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Contact */}
      <Box id="contact" sx={{ scrollMarginTop: 96 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('privacy.sections.contact.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('privacy.sections.contact.body')}</Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
